export interface BzstMatchCodeInfo {
  code: string
  label: string
  meaning: string
  description: string
  color: 'success' | 'error' | 'neutral' | 'warning'
  icon: string
}

export const BZST_MATCH_CODES: Record<string, BzstMatchCodeInfo> = {
  A: {
    code: 'A',
    label: 'Stimmt überein',
    meaning: 'Angaben stimmen überein',
    description:
      'Die übermittelten Daten stimmen exakt mit den im amtlichen Register des EU-Mitgliedstaates hinterlegten Daten überein.',
    color: 'success',
    icon: 'i-lucide-check-circle-2',
  },
  B: {
    code: 'B',
    label: 'Abweichung',
    meaning: 'Angaben stimmen nicht überein',
    description:
      'Die übermittelten Daten weichen von den im amtlichen Register des EU-Mitgliedstaates hinterlegten Daten ab.',
    color: 'error',
    icon: 'i-lucide-x-circle',
  },
  C: {
    code: 'C',
    label: 'Nicht angefragt',
    meaning: 'Nicht angefragt',
    description:
      'Dieses Merkmal wurde in der Bestätigungsanfrage nicht angefragt oder war im Anfrage-Datensatz leer.',
    color: 'neutral',
    icon: 'i-lucide-minus-circle',
  },
  D: {
    code: 'D',
    label: 'Nicht mitgeteilt',
    meaning: 'Vom EU-Mitgliedstaat nicht mitgeteilt',
    description:
      'Der angefragte EU-Mitgliedstaat teilt dieses Merkmal aus rechtlichen oder technischen Gründen nicht mit.',
    color: 'warning',
    icon: 'i-lucide-help-circle',
  },
}

export const BZST_CODE_LEGEND: BzstMatchCodeInfo[] = [
  BZST_MATCH_CODES.A,
  BZST_MATCH_CODES.B,
  BZST_MATCH_CODES.C,
  BZST_MATCH_CODES.D,
]

export function getMatchCodeInfo(code?: string): BzstMatchCodeInfo {
  const normalized = (code || '').trim().toUpperCase()
  if (normalized && BZST_MATCH_CODES[normalized]) {
    return BZST_MATCH_CODES[normalized]
  }
  return {
    code: normalized || '-',
    label: normalized ? `Code ${normalized}` : '-',
    meaning: normalized ? `Code ${normalized}` : 'Nicht geprüft',
    description: 'Kein BZSt-Ergebniswert vorhanden.',
    color: 'neutral',
    icon: 'i-lucide-minus',
  }
}

export function getMatchMeaning(code?: string): string {
  return getMatchCodeInfo(code).meaning
}

export function getMatchLabel(code?: string): string {
  const info = getMatchCodeInfo(code)
  if (info.code === '-') return '-'
  return `${info.code}: ${info.meaning}`
}

export const BZST_STATUS_DESCRIPTIONS: Record<string, string> = {
  'evatr-0000': 'Die angefragte USt-IdNr. ist zum Anfragezeitpunkt gültig.',
  'evatr-0001': 'Bitte bestätigen Sie den Datenschutzhinweis.',
  'evatr-0002': 'Mindestens eins der Pflichtfelder ist nicht besetzt.',
  'evatr-0003':
    'Die angefragte USt-IdNr. ist zum Anfragezeitpunkt gültig. Mindestens eines der Pflichtfelder für eine qualifizierte Bestätigungsanfrage ist nicht besetzt.',
  'evatr-0004':
    'Die anfragende DE USt-IdNr. ist syntaktisch falsch. Sie passt nicht in das deutsche Erzeugungsschema.',
  'evatr-0005': 'Die angegebene angefragte USt-IdNr. ist syntaktisch falsch.',
  'evatr-0006': 'Die anfragende DE USt-IdNr. ist nicht berechtigt eine DE USt-IdNr. anzufragen.',
  'evatr-0007': 'Fehlerhafter Aufruf.',
  'evatr-0008':
    'Die maximale Anzahl von qualifizierten Bestätigungsabfragen für diese Session wurde erreicht.',
  'evatr-0011':
    'Eine Bearbeitung Ihrer Anfrage ist zurzeit nicht möglich. Bitte versuchen Sie es später noch einmal.',
  'evatr-0012':
    'Die angefragte USt-IdNr. ist syntaktisch falsch. Sie passt nicht in das Erzeugungsschema.',
  'evatr-0013':
    'Eine Bearbeitung Ihrer Anfrage ist zurzeit nicht möglich. Bitte versuchen Sie es später noch einmal.',
  'evatr-1001':
    'Eine Bearbeitung Ihrer Anfrage ist zurzeit nicht möglich. Bitte versuchen Sie es später noch einmal.',
  'evatr-1002':
    'Eine Bearbeitung Ihrer Anfrage ist zurzeit nicht möglich. Bitte versuchen Sie es später noch einmal.',
  'evatr-1003':
    'Eine Bearbeitung Ihrer Anfrage ist zurzeit nicht möglich. Bitte versuchen Sie es später noch einmal.',
  'evatr-1004':
    'Eine Bearbeitung Ihrer Anfrage ist zurzeit nicht möglich. Bitte versuchen Sie es später noch einmal.',
  'evatr-2001': 'Die angefragte USt-IdNr. ist zum Anfragezeitpunkt nicht vergeben.',
  'evatr-2002':
    'Die angefragte USt-IdNr. ist zum Anfragezeitpunkt nicht gültig. Sie ist erst gültig ab dem Datum im Feld gueltigAb.',
  'evatr-2003': 'Das angegebene Länderkennzeichen der angefragten USt-IdNr. ist nicht gültig.',
  'evatr-2004':
    'Eine Bearbeitung Ihrer Anfrage ist zurzeit nicht möglich. Bitte versuchen Sie es später noch einmal.',
  'evatr-2005': 'Die angegebene eigene DE USt-IdNr. ist zum Anfragezeitpunkt nicht gültig.',
  'evatr-2006': 'Die angefragte USt-IdNr. ist zum Anfragezeitpunkt nicht mehr gültig.',
  'evatr-2007':
    'Bei der Verarbeitung der Daten aus dem angefragten EU-Mitgliedstaat ist ein Fehler aufgetreten.',
  'evatr-2008':
    'Die angefragte USt-IdNr. ist zum Anfragezeitpunkt gültig (Besonderheit liegt vor).',
  'evatr-2011':
    'Eine Bearbeitung Ihrer Anfrage ist zurzeit nicht möglich. Bitte versuchen Sie es später noch einmal.',
  'evatr-3011':
    'Eine Bearbeitung Ihrer Anfrage ist zurzeit nicht möglich. Bitte versuchen Sie es später noch einmal.',
  'MISSING_VAT_ID': 'Es ist keine USt-IdNr. hinterlegt.',
  'FORMAT_ERROR':
    'Die USt-IdNr. entspricht nicht dem deutschen Format DE gefolgt von 9 Ziffern (^DE\\d{9}$).',
  'FORMALLY_VALID':
    'Format entspricht dem deutschen Schema (^DE\\d{9}$). Keine amtliche Bestätigung durch BZSt.',
  'FORMALLY_VALID_NO_OFFICIAL_CHECK':
    'Format entspricht dem deutschen Schema (^DE\\d{9}$). Keine amtliche Bestätigung durch BZSt.',
  'FORMALLY_INVALID':
    'Die USt-IdNr. entspricht nicht dem deutschen Format DE gefolgt von 9 Ziffern (^DE\\d{9}$).',
}

export function formatFormalStatusCode(code?: string): string {
  if (!code) return '-'
  switch (code) {
    case 'FORMALLY_VALID':
    case 'FORMALLY_VALID_NO_OFFICIAL_CHECK':
      return 'Inland: Gültig'
    case 'MISSING_VAT_ID':
      return 'Keine USt-IdNr.'
    case 'FORMAT_ERROR':
    case 'FORMALLY_INVALID':
      return 'Inland: Ungültig'
    default:
      return code
  }
}

export const VAT_TRIGGER_LABELS: Record<string, string> = {
  NEUANLAGE: 'Neuanlage',
  AENDERUNG: 'Änderung',
  WIEDERHOLUNG: 'Wiederholung',
  VORGANG: 'Vorgang',
  MANUELL: 'Manuell',
}

export function formatTrigger(code?: string): string {
  if (!code) return '-'
  return VAT_TRIGGER_LABELS[code] || code
}

/** A history entry is a Vorgang check if it was triggered by a Vorgang or references one (legacy entries have no number). */
export function isTransactionCheck(item: { trigger?: string; transactionNo?: string }): boolean {
  return item.trigger === 'VORGANG' || !!item.transactionNo
}

export function getBzstStatusDescription(code?: string): string {
  if (!code) return ''
  return BZST_STATUS_DESCRIPTIONS[code] || `BZSt-Status: ${code}`
}

export interface VatStatusBadgeInfo {
  label: string
  color: 'success' | 'error' | 'warning' | 'neutral'
  icon: string
  tooltip: string
}

export function formatVatTimestamp(timestamp?: string): string {
  if (!timestamp) return ''
  try {
    const d = new Date(timestamp)
    if (isNaN(d.getTime())) return timestamp
    return d.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return timestamp
  }
}

export function getVatStatusBadgeInfo(summary?: {
  status?: string
  statusDisplay?: string
  valid?: boolean
  validationType?: string
  interface?: string
  lastCheckTimestamp?: string
  testedVatId?: string
} | null): VatStatusBadgeInfo {
  if (!summary || !summary.status || summary.status === 'NOT_CHECKED') {
    return {
      label: 'Nicht geprüft',
      color: 'neutral',
      icon: 'i-lucide-minus',
      tooltip: 'Noch keine USt-IdNr.-Prüfung für diese Adresse durchgeführt',
    }
  }

  const dateStr = formatVatTimestamp(summary.lastCheckTimestamp)
  const timePrefix = dateStr ? `Letzte Prüfung: ${dateStr} Uhr – ` : ''

  // No check performed (address outside the EU)
  if (summary.validationType === 'NONE') {
    return {
      label: 'Keine EU-Adresse',
      color: 'neutral',
      icon: 'i-lucide-minus',
      tooltip: `${timePrefix}Adresse außerhalb der EU – keine USt-IdNr.-Prüfung möglich.`,
    }
  }

  const isFormal =
    summary.validationType === 'FORMAL' ||
    summary.interface === 'REGEX_DE_PATTERN' ||
    summary.status === 'FORMALLY_VALID' ||
    summary.status === 'FORMALLY_VALID_NO_OFFICIAL_CHECK' ||
    summary.status === 'FORMALLY_INVALID' ||
    summary.status === 'MISSING_VAT_ID'

  if (isFormal) {
    if (summary.valid) {
      return {
        label: 'Inland: Gültig',
        color: 'success',
        icon: 'i-lucide-check-circle-2',
        tooltip: `${timePrefix}Formelle Inlandsprüfung: Format entspricht dem deutschen Schema (^DE\\d{9}$).`,
      }
    }
    if (summary.status === 'MISSING_VAT_ID' || (!summary.testedVatId && !summary.valid)) {
      return {
        label: 'Keine USt-IdNr.',
        color: 'error',
        icon: 'i-lucide-alert-circle',
        tooltip: `${timePrefix}Für diese Rechnungsanschrift ist keine USt-IdNr. hinterlegt.`,
      }
    }
    return {
      label: 'Inland: Ungültig',
      color: 'error',
      icon: 'i-lucide-x-circle',
      tooltip: `${timePrefix}Die USt-IdNr. entspricht nicht dem deutschen Format (^DE\\d{9}$).`,
    }
  }

  switch (summary.status) {
    case 'OFFICIALLY_CONFIRMED':
      return {
        label: 'Ausland: Gültig',
        color: 'success',
        icon: 'i-lucide-check-circle-2',
        tooltip: `${timePrefix}USt-IdNr. ist amtlich gültig und alle Adressdaten stimmen überein.`,
      }
    case 'PARTIALLY_CONFIRMED':
      return {
        label: 'Ausland: Eingeschränkt gültig',
        color: 'warning',
        icon: 'i-lucide-alert-triangle',
        tooltip: `${timePrefix}USt-IdNr. ist gültig, aber mindestens ein Adressbestandteil weicht vom amtlichen EU-Register ab.`,
      }
    case 'OFFICIALLY_NOT_CONFIRMED':
      return {
        label: 'Ausland: Ungültig',
        color: 'error',
        icon: 'i-lucide-x-circle',
        tooltip: `${timePrefix}Die USt-IdNr. ist beim BZSt ungültig, abgelaufen oder nicht vergeben.`,
      }
    case 'CHECK_NOT_POSSIBLE':
    case 'TECHNICAL_ERROR':
      return {
        label: summary.statusDisplay || 'Prüffehler',
        color: 'error',
        icon: 'i-lucide-alert-circle',
        tooltip: `${timePrefix}${summary.statusDisplay || 'Prüfung konnte technisch nicht durchgeführt werden.'}`,
      }
    default:
      if (summary.valid) {
        return {
          label: summary.statusDisplay || 'Ausland: Gültig',
          color: 'success',
          icon: 'i-lucide-check-circle-2',
          tooltip: `${timePrefix}${summary.statusDisplay || 'Ausland: Gültig'}`,
        }
      }
      return {
        label: summary.statusDisplay || 'Ausland: Ungültig',
        color: 'error',
        icon: 'i-lucide-x-circle',
        tooltip: `${timePrefix}${summary.statusDisplay || 'Ausland: Ungültig'}`,
      }
  }
}
